export const DISPLAY_AS = {
  COUNT: { label: 'Count', value: 'count' },
  PERCENT: { label: 'Percentages', value: 'pct' },
};

export const DISPLAY_AS_OPTIONS = [DISPLAY_AS.PERCENT, DISPLAY_AS.COUNT];

// A question's answer options live under `answers` on some question shapes and
// `options` on others. A plain `answers || options` is wrong when `answers` is
// an empty array (arrays are truthy, so it never falls through to a populated
// `options`); check length so the two sources are tried in order.
export const getQuestionOptions = (question) =>
  question?.answers?.length ? question.answers : question?.options || [];

export const getConceptImagesForQuestion = ({ question, quotaGroupConceptImages = [] }) => {
  if (question?.concepts?.length > 0) {
    const filteredConcepts = question.concepts
      .filter((c) => c.image?.cdnUrl)
      .map((concept) => ({
        alt: concept.name?.trim() || 'Concept',
        caption: concept.name?.trim() || 'Concept',
        id: concept.id,
        name: concept.name?.trim() || 'Concept',
        src: concept.image.cdnUrl,
      }));
    if (filteredConcepts.length > 0) {
      return filteredConcepts;
    }
  }
  if (question?.showConceptImage && quotaGroupConceptImages.length > 0) {
    return quotaGroupConceptImages;
  }
  return [];
};

export const sanitizeQuestionText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s*\[concept_image\]\s*/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Maps the `globalFilters` payload + survey questions into the GlobalFilters
 * component's `Facet[]` contract. Trip attributes are absent — the payload
 * doesn't carry them yet; the component supports them once it emits
 * `category: 'trip'` facets.
 *
 * @param {Object} args
 * @param {Object} [args.globalFilters] - transformed globalFilters payload
 * @param {string[]} [args.optionFilterableTypes] - question types that support option filtering
 * @param {Object[]} [args.surveyQuestions] - ordered survey questions
 * @return {Object[]} Facet[]
 */
export const buildGlobalFilterFacets = ({
  globalFilters,
  optionFilterableTypes = [],
  surveyQuestions = [],
}) => {
  const facets = [];

  const quotaGroups = globalFilters?.quotaGroups || [];
  if (quotaGroups.length > 1) {
    facets.push({
      category: 'quota',
      comparable: true,
      filterable: true,
      id: 'quota_group',
      label: 'Quota groups',
      options: quotaGroups.map(({ instantSurveyGroupRef, name }) => ({
        label: name,
        value: instantSurveyGroupRef,
      })),
    });
  }

  (globalFilters?.demographicFilters || []).forEach(({ compareBy, id, label, options }) => {
    facets.push({
      category: 'demographic',
      comparable: Boolean(compareBy),
      filterable: true,
      id,
      label,
      options: (options || []).map((option) => ({
        count: option.count,
        label: option.label,
        value: option.id,
      })),
    });
  });

  // `index` is the position in the full question list, so labels stay "Q{n}."
  surveyQuestions.forEach((question, index) => {
    const { id, options, title, type } = question;
    if (!optionFilterableTypes.includes(type) || !(options?.length > 0)) return;
    facets.push({
      category: 'question',
      comparable: true,
      filterable: true,
      id,
      label: `Q${index + 1}. ${title}`,
      options: options.map((option) => ({ label: option.title, value: option.id })),
    });
  });

  return facets;
};

/**
 * Drops the quota-group facet for callers that hide quota filtering entirely
 * (e.g. AskWhyResponses' `hideQuotaGroupFilters`).
 *
 * @param {Object[]} facets - Facet[]
 * @param {boolean} hideQuotaGroupFilters
 * @return {Object[]} Facet[]
 */
export const excludeQuotaGroupFacets = (facets, hideQuotaGroupFilters) =>
  hideQuotaGroupFilters ? facets.filter((facet) => facet.category !== 'quota') : facets;

const CATALOG_TYPE_TO_CATEGORY = {
  demographic: 'demographic',
  question: 'question',
  quota_group: 'quota',
  trip_attribute: 'trip',
};

/**
 * Maps the global-filters catalog response (`sections[].attributes[].values[]`)
 * into the same `Facet[]` contract as `buildGlobalFilterFacets`. The synthetic
 * `overall` attribute and any unknown type are dropped — the CompareByPicker
 * injects its own Overall entry.
 *
 * @param {Object} [catalog] - global-filters catalog response
 * @return {Object[]} Facet[]
 */
export const buildGlobalFilterFacetsFromCatalog = (catalog) =>
  (catalog?.sections || [])
    .flatMap((section) => section?.attributes || [])
    .filter((attribute) => CATALOG_TYPE_TO_CATEGORY[attribute?.type] && attribute.id != null)
    .map((attribute) => ({
      category: CATALOG_TYPE_TO_CATEGORY[attribute.type],
      comparable: Boolean(attribute.compare_supported),
      filterable: Boolean(attribute.filter_supported),
      id: String(attribute.id),
      label: attribute.label,
      options: (attribute.values || []).map((option) => ({
        count: option.count,
        label: option.label,
        value: String(option.id),
      })),
    }));

const ALL_OTHERS_GROUP_NAME = 'All Others';
const COMPARE_VALUE_CAP = 5;

/** Filter param key for a facet. Demographics and trip attributes use the bare
 * catalog id as-is (e.g. `dept`); quota groups use `quota_group`; questions use
 * `question_filter_<qid>`. */
const filterParamKey = (facet) => {
  if (facet.category === 'question') return `question_filter_${facet.id}`;
  if (facet.category === 'quota') return 'quota_group';
  return facet.id;
};

/** Compare-by key. Questions use `question_<id>`; quota groups use the literal
 * `quota_group` the BE checks for, same as the filter key. */
const compareByKey = (facet) => {
  if (facet.category === 'question') return `question_${facet.id}`;
  if (facet.category === 'quota') return 'quota_group';
  return facet.id;
};

/** Question compare values are `option_<id>`-prefixed; others pass through. */
const compareValueToken = (facet, value) =>
  facet.category === 'question' ? `option_${value}` : value;

/** `;` is the delimiter for every filter param, including `quota_group`. */
const joinValues = (values) => values.join(';');

/**
 * Serializes a `GlobalFilterSelection` into reporting-endpoint GET query params.
 * Returns a flat `{ key: value }` map of RAW (not URL-encoded) values — callers
 * pass this straight into the normal Axios `params` request path, which encodes
 * once via its own serializer.
 *
 * `row_operator` is always sent when any filter param is present, since the BE
 * defaults a missing value to OR.
 *
 * @param {Object} [selection] - GlobalFilterSelection ({ rows, logic, compareBy })
 * @param {Object[]} [facets] - the Facet[] the selection references (for category lookup)
 * @return {Object} flat query-param map
 */
export const buildReportingParams = (selection, facets = []) => {
  const { compareBy, logic, rows = [] } = selection || {};
  const facetsById = new Map(facets.map((facet) => [facet.id, facet]));
  const params = {};

  // Filters — one param per row; values OR'd within a key.
  rows.forEach((row) => {
    const facet = facetsById.get(row.facetId);
    if (!facet || !(row.values?.length > 0)) return;
    params[filterParamKey(facet)] = joinValues(row.values);
  });

  // The BE defaults a missing row_operator to OR, so send it explicitly whenever filters apply.
  if (Object.keys(params).length > 0) params.row_operator = logic === 'OR' ? 'OR' : 'AND';

  // Overall, or a cut with nothing picked yet, emits no comparison params.
  if (compareBy) {
    const facet = facetsById.get(compareBy.facetId);
    const selectedValues = compareBy.selectedValues || [];
    if (facet && selectedValues.length > 0) {
      params.comparison_attribute = compareByKey(facet);
      params.comparison_values = selectedValues
        .slice(0, COMPARE_VALUE_CAP)
        .map((value) => compareValueToken(facet, value))
        .join(';');
      params.comparison_include_all_others = compareBy.includeAllOthers ? 'true' : 'false';
    }
  }

  return params;
};

/**
 * Keeps the reporting query and the comparison metadata consumed by charts,
 * summaries, and exports derived from the same redesign selection.
 *
 * @param {Object} [selection] - GlobalFilterSelection
 * @param {Object[]} [facets] - Facet[]
 * @return {{query: Object, selectedCompareBy: Object|null}}
 */
export const buildReportingState = (selection, facets = []) => {
  const query = buildReportingParams(selection, facets);
  const compareBy = selection?.compareBy;
  const facet = facets.find(({ id }) => id === compareBy?.facetId);

  if (!facet || !query.comparison_attribute) {
    return { query, selectedCompareBy: null };
  }

  const optionsByValue = new Map(
    facet.options.map(({ label, value }) => [String(value), String(label)])
  );
  const selectedGroups = compareBy.selectedValues.slice(0, COMPARE_VALUE_CAP).map((value) => ({
    id: String(value),
    name: optionsByValue.get(String(value)) || String(value),
  }));
  const groups = compareBy.includeAllOthers
    ? [...selectedGroups, { id: ALL_OTHERS_GROUP_NAME, name: ALL_OTHERS_GROUP_NAME }]
    : selectedGroups;

  return {
    query,
    selectedCompareBy: {
      category: facet.category,
      groups,
      label: facet.label,
      value: query.comparison_attribute,
    },
  };
};
